import {
  type CoursePlan,
  parseCoursePlan as parseBillingCoursePlan,
} from "@/course-billing/plan";

import { getShortCommit } from "./utils";

const MAX_REPOSITORY_LINE_COUNT = 501;

export type { CoursePlan } from "@/course-billing/plan";

interface BuildBillingHrefParams {
  plan?: CoursePlan | null;
  returnTo?: string | null;
}

interface BuildPricingHrefParams {
  returnTo?: string | null;
}

interface BuildRepositoryCommitHrefParams {
  commit: string;
  filePath?: string | null;
  from?: string | null;
  lineNumbers?: readonly number[];
}

interface BuildLearningHrefParams {
  destination: string;
  from?: string | null;
}

export interface LearningOrigin {
  href: string;
  label: string;
}

// Build a billing URL that preserves the selected plan and learning destination.
//
export function buildBillingHref({
  plan,
  returnTo,
}: BuildBillingHrefParams = {}) {
  const searchParams = new URLSearchParams();
  const safeReturnTo = parseCourseReturnTo(returnTo);

  if (plan) {
    searchParams.set("plan", plan);
  }
  if (safeReturnTo) {
    searchParams.set("returnTo", safeReturnTo);
  }

  const query = searchParams.toString();
  return query ? `/billing?${query}` : "/billing";
}

// Build a pricing URL that can return a learner to requested course content.
//
export function buildPricingHref({ returnTo }: BuildPricingHrefParams = {}) {
  const safeReturnTo = parseCourseReturnTo(returnTo);
  if (!safeReturnTo) {
    return "/pricing";
  }

  return `/pricing?${new URLSearchParams({ returnTo: safeReturnTo })}`;
}

export function buildRepositoryCommitHref({
  commit,
  filePath,
  from,
  lineNumbers = [],
}: BuildRepositoryCommitHrefParams) {
  const searchParams = new URLSearchParams();

  if (filePath) {
    searchParams.set("file", filePath);
  }

  const safeFrom = parseCourseReturnTo(from);
  if (safeFrom) {
    searchParams.set("from", safeFrom);
  }

  const lineRanges = formatLineRanges(lineNumbers);
  if (lineRanges) {
    searchParams.set("lines", lineRanges);
  }

  const pathname = `/commits/${getShortCommit(commit)}`;
  const query = searchParams.toString();
  return query ? `${pathname}?${query}` : pathname;
}

// Attach a safe learning origin while preserving destination hashes and query.
//
export function buildLearningHref({
  destination,
  from,
}: BuildLearningHrefParams) {
  const safeFrom = parseCourseReturnTo(from);
  if (!safeFrom) {
    return destination;
  }

  const destinationUrl = new URL(destination, "https://course.local");
  destinationUrl.searchParams.set("from", safeFrom);
  return `${destinationUrl.pathname}${destinationUrl.search}${destinationUrl.hash}`;
}

// Parse a cross-view origin and provide a stable, user-facing return label.
//
export function getLearningOrigin(value: unknown): LearningOrigin | null {
  const href = parseCourseReturnTo(value);
  if (!href) {
    return null;
  }

  const pathname = new URL(href, "https://course.local").pathname;
  if (pathname.startsWith("/course/")) {
    return { href, label: "Back to lesson" };
  }
  if (pathname === "/course") {
    return { href, label: "Back to course" };
  }
  if (pathname.startsWith("/concepts/")) {
    return { href, label: "Back to concept" };
  }
  if (pathname === "/concepts") {
    return { href, label: "Back to concepts" };
  }
  if (pathname === "/commits" || pathname.startsWith("/commits/")) {
    return { href, label: "Back to repository" };
  }

  return null;
}

// Build a reusable origin path without carrying an older origin or receipt.
//
export function buildLearningOriginPath(
  pathname: string,
  search: string,
  hash = "",
) {
  const searchParams = new URLSearchParams(search);
  searchParams.delete("from");
  searchParams.delete("purchase");
  const query = searchParams.toString();
  return `${query ? `${pathname}?${query}` : pathname}${hash}`;
}

// Parse compact repository line groups without filling gaps between them.
//
export function parseRepositoryLineNumbers(value: string | null) {
  if (!value) {
    return [];
  }

  const lineNumbers = new Set<number>();

  for (const lineRange of value.split(",")) {
    const match = /^(\d+)(?:-(\d+))?$/.exec(lineRange);
    if (!match) {
      return [];
    }

    const firstLine = Number(match[1]);
    const lastLine = Number(match[2] ?? match[1]);
    const lineCount = lastLine - firstLine + 1;

    if (
      !Number.isSafeInteger(firstLine) ||
      !Number.isSafeInteger(lastLine) ||
      firstLine < 1 ||
      lastLine < firstLine ||
      lineCount > MAX_REPOSITORY_LINE_COUNT
    ) {
      return [];
    }

    for (let lineNumber = firstLine; lineNumber <= lastLine; lineNumber += 1) {
      lineNumbers.add(lineNumber);
    }

    if (lineNumbers.size > MAX_REPOSITORY_LINE_COUNT) {
      return [];
    }
  }

  return Array.from(lineNumbers).sort((first, second) => first - second);
}

export function buildPurchaseSuccessHref(returnTo: string) {
  const safeReturnTo = parseCourseReturnTo(returnTo) ?? "/course";
  const destination = new URL(safeReturnTo, "https://course.local");
  destination.searchParams.set("purchase", "success");

  return `${destination.pathname}${destination.search}${destination.hash}`;
}

// Parse a known course plan from an untrusted query or form value.
//
export function parseCoursePlan(value: unknown): CoursePlan | null {
  return parseBillingCoursePlan(value);
}

// Parse an internal course destination without allowing an external redirect.
//
export function parseCourseReturnTo(value: unknown) {
  if (typeof value !== "string") {
    return null;
  }

  return /^\/(concepts|course|commits)(?:\/[\da-z-]+)?(?:\?[^#]*)?(?:#.*)?$/i.test(
    value,
  )
    ? value
    : null;
}

// Format exact line selections as compact contiguous groups.
//
function formatLineRanges(lineNumbers: readonly number[]) {
  const sortedLineNumbers = Array.from(new Set(lineNumbers)).sort(
    (first, second) => first - second,
  );
  const firstLine = sortedLineNumbers[0];

  if (firstLine === undefined) {
    return "";
  }

  const lineRanges: string[] = [];
  let rangeStart = firstLine;
  let rangeEnd = firstLine;

  for (const lineNumber of sortedLineNumbers.slice(1)) {
    if (lineNumber === rangeEnd + 1) {
      rangeEnd = lineNumber;
      continue;
    }

    lineRanges.push(formatLineRange(rangeStart, rangeEnd));
    rangeStart = lineNumber;
    rangeEnd = lineNumber;
  }

  lineRanges.push(formatLineRange(rangeStart, rangeEnd));
  return lineRanges.join(",");
}

// Format one contiguous line group as a single line or inclusive range.
//
function formatLineRange(firstLine: number, lastLine: number) {
  return firstLine === lastLine
    ? String(firstLine)
    : `${firstLine}-${lastLine}`;
}
